import { useCallback } from 'react'
import { useMutation } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import { rememberPaymentFlow } from 'utilities/analytics/paymentFlow'
import { setSubscriptionPayment } from 'services/plan'
import useFinishFlow, { FLOWS } from 'utilities/navigation/flow'
import useGatewayHandoff from 'utilities/hooks/useGatewayHandoff'
import { goToGateway } from 'modules/gatewayHandoff'

export default function useSetPayment(moneyPool?: string) {
    const finishFlow = useFinishFlow(FLOWS.fundPlan)
    const { locked, isLocked, lock, hold, release } = useGatewayHandoff()

    const { isLoading, mutate } = useMutation({
        mutationFn: (payload: ISetSubscriptionPaymentPayload) => {
            return setSubscriptionPayment(payload)
        },
        onSuccess: (result, variables) => {
            // Settled out of the wallet: the backend created the Order, booked
            // `hwt → hamyan_pool` and activated the package itself. There is no
            // bank to visit, so there is no `payment_link` to follow and
            // nothing for the gateway-callback page to finish — leaving a
            // `toBePaidTransactions` record behind would hand it work for a
            // payment that is already done.
            if (result?.paid_from_wallet) {
                release()
                posthog.capture('payment_wallet_paid', {
                    entry: 'subscription',
                    amount: variables?.amount,
                    month_count: variables?.monthCount,
                    box_id: variables?.ctxId,
                })
                toast.success('اشتراک صندوق از کیف پول خریداری شد')
                finishFlow(`/fund/${moneyPool ?? variables?.ctxId}`)
                return
            }

            localStorage.setItem(
                'toBePaidTransactions',
                JSON.stringify({ type: 'subscription', data: [] })
            )
            posthog.capture('payment_gateway_redirect', {
                flow_type: 'subscription',
                amount: variables?.amount,
                month_count: variables?.monthCount,
                gateway: variables?.gatewayCode,
                use_wallet: variables?.useWallet,
                has_discount: !!variables?.discountCode,
            })
            rememberPaymentFlow({
                flow_type: 'subscription',
                amount: variables?.amount ?? null,
                gateway: variables?.gatewayCode ?? null,
                box_id: variables?.ctxId ?? null,
                box_type: 'mnp',
                month_count: variables?.monthCount ?? null,
            })
            // `payment_link` is null when the backend settled the purchase
            // outright — handled above. Reaching here without one means the
            // card leg failed, and `replace(null)` navigates to the literal
            // string "null", which is how a payment turns into a blank page.
            if (!result?.payment_link) {
                release()
                posthog.capture('subscription_purchase_failed', {
                    reason: 'no_payment_link',
                })
                toast.error('مشکلی پیش آمده!')
                return
            }
            // The button stays down while the browser is on its way to the
            // bank — see `useGatewayHandoff`.
            hold()
            goToGateway(result.payment_link)
        },
        onError: (error: any) => {
            release()
            posthog.capture('subscription_purchase_failed', {
                status: error?.response?.status,
            })
            // The backend sends actionable Persian text here (e.g. the gateway
            // allowlist rejection, or «موجودی کیف پول شما برای این پرداخت کافی
            // نیست»); show it instead of a generic failure.
            const detail = error?.response?.data?.error
            toast.error(
                typeof detail === 'string' && detail ? detail : 'مشکلی پیش آمده!'
            )
        },
    })
    const pay = useCallback(
        (payload: ISetSubscriptionPaymentPayload) => {
            // Refused by the ref, not by `isLoading`: a second tap in the same
            // tick lands before React has re-rendered `disabled`.
            if (isLocked()) return
            lock()
            mutate(payload)
        },
        [isLocked, lock, mutate]
    )
    return { mutate: pay, isLoading: isLoading || locked }
}
